import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Plus, Search, Edit3, Trash2, X,
    Shield, Users, UserCheck, CheckSquare,
    ChevronRight, CheckCircle2, Circle, Info,
    Clock, RefreshCw
} from 'lucide-react';
import {
    getTaskSettingsRequest,
    createTaskSettingRequest,
    updateTaskSettingRequest,
    deleteTaskSettingRequest
} from '../../../../redux/actions/taskSettingsActions';
import { getAllModulesRequest } from '../../../../redux/actions/moduleActions';
import { getActiveRolesRequest } from '../../../../redux/actions/roleActions';
import { getAllUsersRequest } from '../../../../redux/actions/userActions';
import Select from 'react-select';
import Swal from 'sweetalert2';
import './TaskSettings.css';
import ScrollToTop from '../../../../Ui/UiJs/ScrollToTop';

const TARGET_CONFIG = {
    REQUESTER: {
        label: 'Demandeur',
        description: "L'initiateur de l'événement",
        class: 'ts-badge-blue', cardClass: 'ts-target-card-blue', icon: UserCheck
    },
    MANAGER: {
        label: 'Manager (N+1)',
        description: "Le manager direct de l'utilisateur",
        class: 'ts-badge-purple', cardClass: 'ts-target-card-purple', icon: Users
    },
    HR_ADMIN: {
        label: 'Admin RH',
        description: "Les administrateurs des ressources humaines",
        class: 'ts-badge-red', cardClass: 'ts-target-card-red', icon: Shield
    },
    SPECIFIC_ROLES: {
        label: 'Rôle spécifique',
        description: 'Tous les utilisateurs ayant un ou plusieurs rôles donnés',
        class: 'ts-badge-orange', cardClass: 'ts-target-card-orange', icon: Shield
    },
    SPECIFIC_USERS: {
        label: 'Utilisateur(s)',
        description: 'Sélection manuelle d\'un ou plusieurs utilisateurs',
        class: 'ts-badge-green', cardClass: 'ts-target-card-green', icon: Users
    },
    ALL_USERS: {
        label: 'Tous les utilisateurs',
        description: 'Toute l\'entreprise',
        class: 'ts-badge-gray', cardClass: 'ts-target-card-gray', icon: Users
    }
};

const ACTION_TYPES = [
    { value: 'APPROVE_REJECT', label: 'Approbation / Rejet' },
    { value: 'FILL_FORM', label: 'Remplir un formulaire' },
    { value: 'SIGN_DOCUMENT', label: 'Signer un document' },
    { value: 'ACKNOWLEDGE', label: 'Prendre connaissance' },
    { value: 'CUSTOM', label: 'Action personnalisée' }
];

const PRIORITIES = [
    { value: 'LOW', label: 'Basse' },
    { value: 'MEDIUM', label: 'Moyenne' },
    { value: 'HIGH', label: 'Haute' },
    { value: 'URGENT', label: 'Urgente' }
];

const EMPTY_FORM = {
    eventCode: '', eventName: '',
    titleTemplate: '', descriptionTemplate: '',
    routePath: '', icon: 'check-square',
    targetTypes: ['MANAGER'],
    moduleId: '', targetRoleIds: [], targetUserIds: [],
    priority: 'MEDIUM', actionType: 'APPROVE_REJECT',
    slaHours: 48, isActive: true
};

const TaskSettings = () => {
    const dispatch = useDispatch();
    const { settings, loading } = useSelector(state => state.taskSettings || { settings: [], loading: false });
    const { modules } = useSelector(state => state.module || { modules: [] });
    const { roles } = useSelector(state => state.role || { roles: [] });
    const { users } = useSelector(state => state.user || { users: [] });

    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSetting, setEditingSetting] = useState(null);
    const [modalStep, setModalStep] = useState(1);
    const [formData, setFormData] = useState(EMPTY_FORM);

    const refreshData = () => {
        dispatch(getTaskSettingsRequest());
        dispatch(getAllModulesRequest());
        dispatch(getActiveRolesRequest());
        dispatch(getAllUsersRequest());
    };

    useEffect(() => {
        refreshData();
    }, [dispatch]);

    const filtered = useMemo(() => (settings || []).filter(s => {
        const matchesQ = !q ||
            s.eventCode?.toLowerCase().includes(q.toLowerCase()) ||
            s.eventName?.toLowerCase().includes(q.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' ||
            (statusFilter === 'ACTIVE' && s.isActive) ||
            (statusFilter === 'INACTIVE' && !s.isActive);
        return matchesQ && matchesStatus;
    }), [settings, q, statusFilter]);

    const handleOpenModal = (setting = null) => {
        if (setting) {
            setEditingSetting(setting);
            setFormData({
                eventCode: setting.eventCode || '',
                eventName: setting.eventName || '',
                titleTemplate: setting.titleTemplate || '',
                descriptionTemplate: setting.descriptionTemplate || '',
                routePath: setting.routePath || '',
                icon: setting.icon || '',
                targetTypes: setting.targetTypes || ['MANAGER'],
                moduleId: setting.moduleId || '',
                targetRoleIds: setting.targetRoles?.map(r => r.id) || [],
                targetUserIds: setting.targetUsers?.map(u => u.id) || [],
                priority: setting.priority || 'MEDIUM',
                actionType: setting.actionType || 'APPROVE_REJECT',
                slaHours: setting.slaHours || 48,
                isActive: setting.isActive
            });
        } else {
            setEditingSetting(null);
            setFormData(EMPTY_FORM);
        }
        setModalStep(1);
        setIsModalOpen(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingSetting) dispatch(updateTaskSettingRequest(editingSetting.id, formData));
        else dispatch(createTaskSettingRequest(formData));
        setIsModalOpen(false);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Supprimer la règle ?',
            text: "Cette action désactivera la génération de cette tâche.",
            icon: 'warning', showCancelButton: true,
            confirmButtonColor: '#2a5ca9', cancelButtonColor: '#9ca3af',
            confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler'
        }).then(r => { if (r.isConfirmed) dispatch(deleteTaskSettingRequest(id)); });
    };

    const handleToggleActive = (setting) => {
        dispatch(updateTaskSettingRequest(setting.id, {
            ...setting,
            targetRoleIds: setting.targetRoles?.map(r => r.id) || [],
            targetUserIds: setting.targetUsers?.map(u => u.id) || [],
            isActive: !setting.isActive
        }));
    };

    const toggleTargetType = (key) => {
        const current = formData.targetTypes;
        const next = current.includes(key) ? current.filter(t => t !== key) : [...current, key];
        const stillNeedsRoles = next.some(t => ['SPECIFIC_ROLES', 'SPECIFIC_USERS'].includes(t));
        setFormData({ ...formData, targetTypes: next, targetRoleIds: stillNeedsRoles ? formData.targetRoleIds : [], targetUserIds: stillNeedsRoles ? formData.targetUserIds : [] });
    };

    const toggleRole = (id) => {
        const next = formData.targetRoleIds.includes(id) ? formData.targetRoleIds.filter(x => x !== id) : [...formData.targetRoleIds, id];
        setFormData({ ...formData, targetRoleIds: next, targetUserIds: [] });
    };

    const needsRolePicker = formData.targetTypes.some(t => ['SPECIFIC_ROLES', 'SPECIFIC_USERS'].includes(t));
    const needsUserPicker = formData.targetTypes.some(t => ['SPECIFIC_ROLES', 'SPECIFIC_USERS'].includes(t));
    const hasOnlySimpleTargets = formData.targetTypes.length > 0 && formData.targetTypes.every(t => ['REQUESTER', 'MANAGER', 'HR_ADMIN', 'ALL_USERS'].includes(t));

    const availableRoles = useMemo(() => formData.moduleId ? roles.filter(r => r.moduleId === parseInt(formData.moduleId)) : roles, [roles, formData.moduleId]);
    const availableUsersForSelect = useMemo(() => {
        let filtered = users;
        if (formData.moduleId) filtered = filtered.filter(u => u.roles?.some(r => r.moduleId === parseInt(formData.moduleId)));
        if (formData.targetRoleIds?.length > 0) filtered = filtered.filter(u => u.roles?.some(r => formData.targetRoleIds.includes(r.id)));
        return filtered.map(u => ({ value: u.id, label: u.fullName }));
    }, [users, formData.moduleId, formData.targetRoleIds]);

    const step2Complete = formData.targetTypes.length > 0 && (hasOnlySimpleTargets || (formData.targetTypes.includes('SPECIFIC_ROLES') && formData.targetRoleIds.length > 0) || (formData.targetTypes.includes('SPECIFIC_USERS') && formData.targetUserIds.length > 0));

    const renderTargetBadges = (setting) => {
        const types = setting.targetTypes || [];
        return (
            <div className="ts-badge-list">
                {types.map(type => {
                    const config = TARGET_CONFIG[type];
                    const Icon = config?.icon || CheckSquare;
                    return (
                        <span key={type} className={`ts-badge ${config?.class || 'ts-badge-blue'}`}>
                            <Icon size={11} className="me-1" />
                            {config?.label || type}
                            {type === 'SPECIFIC_ROLES' && setting.targetRoles?.length > 0 && <span className="ts-target-sub"> · {setting.targetRoles.length} rôles</span>}
                            {type === 'SPECIFIC_USERS' && setting.targetUsers?.length > 0 && <span className="ts-target-sub"> · {setting.targetUsers.length} util.</span>}
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="ts-page">
            {/* ── Header ─────────────────────────── */}
            <div className="ts-header">
                <div>
                    <h2>Paramétrage des tâches</h2>
                    <p className="ts-subtitle">Gérez la création dynamique des tâches pour vos collaborateurs selon des événements.</p>
                </div>
                <div className="application-logs-header-actions">
                    <button className="ts-btn ts-btn-primary" onClick={() => handleOpenModal()}>
                        <Plus size={14} /> Nouvelle Tâche
                    </button>
                    <button className="ts-btn ts-btn-outline" onClick={refreshData} disabled={loading}>
                        <RefreshCw size={14} className={loading ? "al-spin" : ""} />
                    </button>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────── */}
            <div className="ts-toolbar">
                <div className="ts-search-wrapper">
                    <Search className="ts-search-icon" size={14} />
                    <input type="text" className="ts-search-input"
                           placeholder="Rechercher par événement, titre..."
                           value={q} onChange={e => setQ(e.target.value)} />
                </div>
                <div className="ts-filters">
                    {['ALL', 'ACTIVE', 'INACTIVE'].map(f => (
                        <button key={f} className={`ts-filter-tab ${statusFilter === f ? 'active' : ''}`}
                                onClick={() => setStatusFilter(f)}>
                            {f === 'ALL' ? 'Tous' : f === 'ACTIVE' ? 'Actifs' : 'Inactifs'}
                        </button>
                    ))}
                </div>
                <div className="ts-count">
                    {filtered.length} règle{filtered.length > 1 ? 's' : ''}
                </div>
            </div>

            {/* ── Table ──────────────────────────── */}
            <div className="ts-table-wrapper">
                {loading ? (
                    <div className="al-loading">
                        <div className="al-spinner" />
                        <span>Chargement...</span>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="al-empty">Aucune règle de tâche trouvée.</div>
                ) : (
                    <table className="ts-table">
                        <thead>
                        <tr>
                            <th>Événement</th>
                            <th>Cible(s)</th>
                            <th>Action</th>
                            <th>Délai (SLA)</th>
                            <th>Statut</th>
                            <th className="text-end">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.map(setting => (
                            <tr key={setting.id}>
                                <td>
                                    <div className="ts-icon-cell">
                                        <div className="ts-avatar"><CheckSquare size={16} /></div>
                                        <div>
                                            <div className="ts-badge ts-badge-blue" style={{ fontSize: '10px', marginBottom: '2px' }}>{setting.eventCode}</div>
                                            <div style={{ fontWeight: 700, color: '#1a3b6e', fontSize: '12px' }}>{setting.eventName}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>{renderTargetBadges(setting)}</td>
                                <td><span className="ts-badge ts-badge-outline">{ACTION_TYPES.find(a => a.value === setting.actionType)?.label || setting.actionType}</span></td>
                                <td>
                                    <div className="d-flex align-items-center gap-1" style={{ color: '#6e91c1', fontSize: '11px', fontWeight: 600 }}>
                                        <Clock size={12} /> {setting.slaHours ? `${setting.slaHours}h` : 'N/A'}
                                    </div>
                                </td>
                                <td>
                                    <div className={`ts-toggle ${setting.isActive ? 'on' : ''}`} onClick={() => handleToggleActive(setting)}>
                                        <div className="ts-toggle-box"><div className="ts-toggle-knob" /></div>
                                        <span className="ts-toggle-label">{setting.isActive ? 'Actif' : 'Inactif'}</span>
                                    </div>
                                </td>
                                <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                        <button className="ts-btn ts-btn-outline" style={{ padding: '6px' }} onClick={() => handleOpenModal(setting)}>
                                            <Edit3 size={14} style={{ color: '#2a5ca9' }} />
                                        </button>
                                        <button className="ts-btn ts-btn-outline" style={{ padding: '6px' }} onClick={() => handleDelete(setting.id)}>
                                            <Trash2 size={14} style={{ color: '#ee645f' }} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ===== MODAL ===== */}
            {isModalOpen && (
                <div className="ts-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="ts-modal" onClick={e => e.stopPropagation()}>
                        <div className="ts-modal-header">
                            <div className="ts-modal-title-group">
                                <h3>{editingSetting ? 'Modifier la Tâche' : 'Nouvelle Tâche'}</h3>
                                <div className="ts-steps">
                                    <button type="button" className={`ts-step ${modalStep === 1 ? 'active' : 'done'}`} onClick={() => setModalStep(1)}>
                                        {modalStep > 1 ? <CheckCircle2 size={14} /> : <span>1</span>}
                                        <span>Paramètres</span>
                                    </button>
                                    <ChevronRight size={14} className="ts-step-sep" />
                                    <button type="button" className={`ts-step ${modalStep === 2 ? 'active' : ''} ${step2Complete && modalStep !== 2 ? 'done' : ''}`} onClick={() => setModalStep(2)}>
                                        {step2Complete && modalStep !== 2 ? <CheckCircle2 size={14} /> : <span>2</span>}
                                        <span>Destinataires</span>
                                    </button>
                                </div>
                            </div>
                            <button className="ts-modal-close" onClick={() => setIsModalOpen(false)}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="ts-modal-form">
                            <div className="ts-modal-body">
                                {modalStep === 1 && (
                                    <div className="ts-form-grid">
                                        <div className="ts-form-group">
                                            <label>Code Événement *</label>
                                            <input type="text" className="ts-input" value={formData.eventCode} onChange={e => setFormData({ ...formData, eventCode: e.target.value })} placeholder="ex: LEAVE_VALIDATION" required />
                                        </div>
                                        <div className="ts-form-group">
                                            <label>Nom de la règle *</label>
                                            <input type="text" className="ts-input" value={formData.eventName} onChange={e => setFormData({ ...formData, eventName: e.target.value })} placeholder="ex: Validation de congé" required />
                                        </div>
                                        <div className="ts-form-group">
                                            <label>Module lié</label>
                                            <select className="ts-select" value={formData.moduleId} onChange={e => setFormData({ ...formData, moduleId: e.target.value })}>
                                                <option value="">Sélectionnez un module</option>
                                                {modules?.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="ts-form-group">
                                            <label>Type d'action *</label>
                                            <select className="ts-select" value={formData.actionType} onChange={e => setFormData({ ...formData, actionType: e.target.value })} required>
                                                {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="ts-form-group">
                                            <label>Priorité</label>
                                            <select className="ts-select" value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}>
                                                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="ts-form-group">
                                            <label>Délai SLA (en heures)</label>
                                            <input type="number" className="ts-input" value={formData.slaHours} onChange={e => setFormData({ ...formData, slaHours: e.target.value ? parseInt(e.target.value) : '' })} placeholder="ex: 48" />
                                        </div>
                                        <div className="ts-form-group full">
                                            <label>Titre de la tâche *</label>
                                            <input type="text" className="ts-input" value={formData.titleTemplate} onChange={e => setFormData({ ...formData, titleTemplate: e.target.value })} placeholder="ex: Valider le congé de {{employeeName}}" required />
                                        </div>
                                        <div className="ts-form-group full">
                                            <label>Description de la tâche</label>
                                            <textarea className="ts-textarea" rows="3" value={formData.descriptionTemplate} onChange={e => setFormData({ ...formData, descriptionTemplate: e.target.value })} placeholder="Explication ou détails contextuels..." />
                                        </div>
                                        <div className="ts-form-group full">
                                            <label>Route d'action (URL)</label>
                                            <input type="text" className="ts-input" value={formData.routePath} onChange={e => setFormData({ ...formData, routePath: e.target.value })} placeholder="ex: /leaves/approve/{{entityId}}" />
                                        </div>
                                    </div>
                                )}
                                {modalStep === 2 && (
                                    <div className="ts-target-section">
                                        <div className="ts-section-header">
                                            <div className="ts-section-label">Qui doit traiter cette tâche ?</div>
                                            <span className="ts-multi-hint"><CheckCircle2 size={11} /> Sélection multiple possible</span>
                                        </div>
                                        <div className="ts-target-cards">
                                            {Object.entries(TARGET_CONFIG).map(([key, config]) => {
                                                const Icon = config.icon;
                                                const isSelected = formData.targetTypes.includes(key);
                                                return (
                                                    <button type="button" key={key} className={`ts-target-card ${isSelected ? 'selected' : ''}`} onClick={() => toggleTargetType(key)}>
                                                        <div className="ts-target-card-icon"><Icon size={18} /></div>
                                                        <div className="ts-target-card-content">
                                                            <span className="ts-target-card-label">{config.label}</span>
                                                            <span className="ts-target-card-desc">{config.description}</span>
                                                        </div>
                                                        <div className="ts-target-card-check">{isSelected ? <CheckCircle2 size={18} /> : <Circle size={18} />}</div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {needsRolePicker && (
                                            <div className="ts-picker-block mt-4">
                                                <div className="ts-picker-header"><Shield size={14} /> <span>Rôles destinataires *</span></div>
                                                <Select isMulti options={availableRoles.map(r => ({ value: r.id, label: r.name || r.roleName }))} value={availableRoles.filter(r => formData.targetRoleIds.includes(r.id)).map(r => ({ value: r.id, label: r.name || r.roleName }))} onChange={sel => setFormData({ ...formData, targetRoleIds: sel ? sel.map(s => s.value) : [], targetUserIds: [] })} placeholder="Choisir un ou plusieurs rôles..." menuPortalTarget={document.body} />
                                            </div>
                                        )}
                                        {needsUserPicker && (
                                            <div className="ts-picker-block mt-4">
                                                <div className="ts-picker-header"><Users size={14} /> <span>Utilisateurs destinataires *</span></div>
                                                <Select isMulti options={availableUsersForSelect} value={availableUsersForSelect.filter(o => formData.targetUserIds.includes(o.value))} onChange={sel => setFormData({ ...formData, targetUserIds: sel ? sel.map(s => s.value) : [] })} placeholder="Sélectionner des utilisateurs..." menuPortalTarget={document.body} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="ts-modal-footer">
                                {modalStep === 1 ? (
                                    <>
                                        <button type="button" className="ts-btn ts-btn-outline" onClick={() => setIsModalOpen(false)}>Annuler</button>
                                        <button type="button" className="ts-btn ts-btn-primary" onClick={() => setModalStep(2)} disabled={!formData.eventCode || !formData.eventName || !formData.titleTemplate}>Suivant <ChevronRight size={15} /></button>
                                    </>
                                ) : (
                                    <>
                                        <button type="button" className="ts-btn ts-btn-outline" onClick={() => setModalStep(1)}>← Retour</button>
                                        <button type="submit" className="ts-btn ts-btn-primary" disabled={!step2Complete}>{editingSetting ? 'Mettre à jour' : 'Créer'}</button>
                                    </>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <ScrollToTop />
        </div>
    );
};

export default TaskSettings;
